import { ConsoleLogger, HttpServiceBase, TaggedUrl, Tags } from '@skylab-shared/core';
import { fillPlaceholders, Singleton } from '@skylab-shared/utils';
import { AxiosRequestConfig } from 'axios';

export type Languages = 'pl' | 'uk' | 'ru' | 'en' | 'de';
export const AVAILABLE_LANGUAGES: Languages[] = ['pl', 'ru', 'uk', 'en', 'de'];
export const DEFAULT_LANGUAGE: Languages = 'pl';
export const LANGUAGE_LOCALSTORAGE_KEY = 'lang';

@Singleton
export class UserLanguageLoaderBase {
    private _language: Languages;
    private readonly url = TaggedUrl.join('user/:action', [Tags.Api]);
    private readonly languageRequestKey = 'user_language';
    private readonly logger: ConsoleLogger;

    public constructor() {
        this.logger = new ConsoleLogger(this.constructor.name);
    }

    public async loadLanguageAsync(): Promise<Languages> {
        const localStorageLang = <Languages>localStorage.getItem(LANGUAGE_LOCALSTORAGE_KEY);
        const viewSettingsLanguage = await this.getSavedLanguageAsync();
        const storedLanguage = localStorageLang || viewSettingsLanguage || this.getBrowserLanguage();
        this._language = this.isLanguageCorrect(storedLanguage) ? storedLanguage : DEFAULT_LANGUAGE;

        if (!this.isLanguageCorrect(localStorageLang)) localStorage.setItem(LANGUAGE_LOCALSTORAGE_KEY, this._language);
        if (!this.isLanguageCorrect(viewSettingsLanguage)) await this.saveUserLanguageAsync(this._language);
        return this._language;
    }

    private isLanguageCorrect(language: Languages | null): boolean {
        return language !== null && AVAILABLE_LANGUAGES.includes(language);
    }

    private getBrowserLanguage(): Languages {
        const lang = navigator['language'];
        switch (lang) {
            case 'pl':
            case 'pl-PL':
                return 'pl';
            case 'uk':
            case 'uk-UA':
                return 'uk';
            case 'ru':
            case 'ru-RU':
                return 'ru';
            case 'en':
            case 'en-EN':
                return 'en';
                case 'de':
            case 'de-DE':
                return 'de';
            default:
                return 'pl';
        }
    }

    public async saveUserLanguageAsync(lang: Languages): Promise<void> {
        try {
            const language = this.isLanguageCorrect(lang) ? lang : DEFAULT_LANGUAGE;
            const url = fillPlaceholders(this.url, { action: 'settings' });
            const config: AxiosRequestConfig = {
                headers: {
                    'Content-Type': 'application/json',
                    'Access-Control-Allow-Origin': '*',
                },
                params: { key: this.languageRequestKey },
            };
            await HttpServiceBase.put<void>(url, { value: JSON.stringify(language) }, config);
            localStorage.setItem(LANGUAGE_LOCALSTORAGE_KEY, language);
        } catch (err) {
            this.logger.error(err);
        }
    }

    private async getSavedLanguageAsync(): Promise<Languages | null> {
        try {
            const url = fillPlaceholders(this.url, { action: 'settings' });
            const config: AxiosRequestConfig = {
                params: { key: this.languageRequestKey }
            };
            const response = await HttpServiceBase.get<{value: Languages}>(url, config);
            return JSON.parse(response.data.value);
        } catch (err) {
            this.logger.error(err);
        }

        return null;
    }

    public get language(): Languages {
        if (!this._language) {
            throw new Error(`User language not yet loaded`);
        }
        return this._language;
    }
}

export const UserLanguageLoader = new UserLanguageLoaderBase();
